import { CartStorage } from "@/libs/cart-storage";
import { storefrontClient } from "../client";

const formatCartId = (id: string | null): string | null => {
  if (!id) return null;

  if (id && !id.startsWith("gid://shopify/Cart/")) {
    return `gid://shopify/Cart/${id}`;
  }
  return id;
};

export const createCart = async () => {
  try {
    const { data } = await storefrontClient.request(
      `
        mutation CreateCart {
          cartCreate {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          id
                          title
                          handle
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
    );

    if (data?.cartCreate?.cart?.id) {
      CartStorage.saveCartToken(data.cartCreate.cart.id);
    }

    return data?.cartCreate?.cart;
  } catch (error) {
    console.error("Error creating cart:", error);
    throw error;
  }
};

export const getCart = async (cartId?: string) => {
  try {
    const id = cartId || CartStorage.getCartToken();

    if (!id) {
      return await createCart();
    }

    const formattedId = formatCartId(id);
    if (!formattedId) {
      return await createCart();
    }

    const { data } = await storefrontClient.request(
      `
        query GetCart($cartId: ID!) {
          cart(id: $cartId) {
            id
            checkoutUrl
            totalQuantity
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      product {
                        id
                        title
                        handle
                      }
                      price {
                        amount
                        currencyCode
                      }
                      image {
                        url
                        altText
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {
        variables: {
          cartId: formattedId,
        },
      },
    );

    if (data?.cart?.id) {
      CartStorage.saveCartToken(data.cart.id);
    }

    return data?.cart;
  } catch (error) {
    console.error("Error fetching cart:", error);

    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("Cart not found")) {
      return await createCart();
    }

    throw error;
  }
};

export const addToCart = async (
  variantId: string,
  quantity: number,
  cartId?: string,
) => {
  try {
    let id = cartId || CartStorage.getCartToken();

    if (!id) {
      const newCart = await createCart();
      id = newCart.id;

      if (id) {
        CartStorage.saveCartToken(id);
      }
    }

    const formattedId = formatCartId(id);
    if (!formattedId) {
      throw new Error("Invalid cart ID");
    }

    const { data } = await storefrontClient.request(
      `
        mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          id
                          title
                          handle
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
      {
        variables: {
          cartId: formattedId,
          lines: [
            {
              merchandiseId: variantId,
              quantity: quantity,
            },
          ],
        },
      },
    );

    if (data?.cartLinesAdd?.cart?.id) {
      CartStorage.saveCartToken(data.cartLinesAdd.cart.id);
    }

    return data?.cartLinesAdd?.cart;
  } catch (error) {
    console.error("Error adding to cart:", error);
    throw error;
  }
};

export const updateCartItem = async (
  lineId: string,
  quantity: number,
  cartId?: string,
) => {
  try {
    const id = cartId || CartStorage.getCartToken();

    if (!id) {
      throw new Error("No cart ID available");
    }

    const formattedId = formatCartId(id);
    if (!formattedId) {
      throw new Error("Invalid cart ID");
    }

    const { data } = await storefrontClient.request(
      `
        mutation UpdateCartItem($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          id
                          title
                          handle
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
      {
        variables: {
          cartId: formattedId,
          lines: [
            {
              id: lineId,
              quantity: quantity,
            },
          ],
        },
      },
    );

    if (data?.cartLinesUpdate?.cart?.id) {
      CartStorage.saveCartToken(data.cartLinesUpdate.cart.id);
    }

    return data?.cartLinesUpdate?.cart;
  } catch (error) {
    console.error("Error updating cart item:", error);
    throw error;
  }
};

export const removeCartItem = async (lineId: string, cartId?: string) => {
  try {
    const id = cartId || CartStorage.getCartToken();

    if (!id) {
      throw new Error("No cart ID available");
    }

    const formattedId = formatCartId(id);
    if (!formattedId) {
      throw new Error("Invalid cart ID");
    }

    const { data } = await storefrontClient.request(
      `
        mutation RemoveCartItem($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          id
                          title
                          handle
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
      {
        variables: {
          cartId: formattedId,
          lineIds: [lineId],
        },
      },
    );

    if (data?.cartLinesRemove?.cart?.id) {
      CartStorage.saveCartToken(data.cartLinesRemove.cart.id);
    }

    return data?.cartLinesRemove?.cart;
  } catch (error) {
    console.error("Error removing cart item:", error);
    throw error;
  }
};

export const clearCart = async (cartId?: string) => {
  try {
    const id = cartId || CartStorage.getCartToken();

    if (!id) {
      return await createCart();
    }

    const formattedId = formatCartId(id);
    if (!formattedId) {
      throw new Error("Invalid cart ID");
    }

    const currentCart = await getCart(id);

    if (
      !currentCart ||
      !currentCart.lines ||
      !currentCart.lines.edges ||
      currentCart.lines.edges.length === 0
    ) {
      return currentCart;
    }

    const lineIds = currentCart.lines.edges.map((edge: any) => edge.node.id);
    const { data } = await storefrontClient.request(
      `
        mutation ClearCart($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          id
                          title
                          handle
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
      {
        variables: {
          cartId: id,
          lineIds: lineIds,
        },
      },
    );

    return data?.cartLinesRemove?.cart;
  } catch (error) {
    console.error("Error clearing cart:", error);

    return await createCart();
  }
};

export const applyDiscountCode = async (
  discountCode: string,
  cartId?: string,
) => {
  try {
    const id = cartId || CartStorage.getCartToken();

    if (!id) {
      throw new Error("No cart ID available");
    }

    const formattedId = formatCartId(id);
    if (!formattedId) {
      throw new Error("Invalid cart ID");
    }

    const { data } = await storefrontClient.request(
      `
        mutation ApplyDiscountCode($cartId: ID!, $discountCodes: [String!]!) {
          cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
            cart {
              id
              checkoutUrl
              totalQuantity
              discountCodes {
                code
                applicable
              }
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
                totalDutyAmount {
                  amount
                  currencyCode
                }
                checkoutChargeAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        product {
                          id
                          title
                          handle
                        }
                        price {
                          amount
                          currencyCode
                        }
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `,
      {
        variables: {
          cartId: formattedId,
          discountCodes: [discountCode],
        },
      },
    );

    if (data?.cartDiscountCodesUpdate?.cart?.id) {
      CartStorage.saveCartToken(data.cartDiscountCodesUpdate.cart.id);
    }

    return data?.cartDiscountCodesUpdate?.cart;
  } catch (error) {
    console.error("Error applying discount code:", error);
    throw error;
  }
};

export const shopifyGetCart = getCart;
export const shopifyAddToCart = addToCart;
